import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

export type AppNotification = {
  id: string;
  event:
    | "new_order"
    | "new_proposal"
    | "proposal_status"
    | "contract_created"
    | "contract_signed"
    | "contract_ready"
    | "partner_invitation"
    | "partner_request";
  title: string;
  description: string;
  url?: string;
  serviceType?: string;
  serviceName?: string;
  serviceIcon?: string;
  createdAt: string;
  readAt?: string | null;
};

type NotificationsResponse = {
  notifications: AppNotification[];
  unreadCount: number;
};

type NotificationsContextType = {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  limit: number;
  refresh: (nextLimit?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  ingest: (notification: AppNotification) => void;
};

const NotificationsContext = createContext<NotificationsContextType | null>(
  null
);

const API_URL = process.env.REACT_APP_API_URL || "";
const DEFAULT_LIMIT = 20;
/** Matches the backend's default cap; the page needs it to know when to stop offering «Загрузить ещё». */
export const MAX_CAP = 100;

/**
 * Ticket 16 (audit F17). The list and the badge used to be two `useState`s written
 * by separate setters, so every operation was two writes that could disagree — and
 * three of them did: the optimistic update sat in `finally` (a failed POST still
 * marked the item read), the decrement ran whatever the item's state was (and
 * `Header` calls `markRead` on *every* dropdown click, read or not), and `ingest`'s
 * increment sat outside its own de-duplication.
 *
 * One reducer, one transition per operation, each idempotent by construction.
 */
type NotificationsState = {
  items: AppNotification[];
  unreadCount: number;
};

type NotificationsAction =
  | { type: "reset" }
  | { type: "sync"; items: AppNotification[]; unreadCount: number }
  | { type: "ingest"; notification: AppNotification }
  | { type: "read"; ids: string[]; readAt: string }
  | { type: "restoreRead"; id: string; prevReadAt: string | null }
  | { type: "markAllRead"; readAt: string };

const EMPTY_STATE: NotificationsState = { items: [], unreadCount: 0 };

function notificationsReducer(
  state: NotificationsState,
  action: NotificationsAction
): NotificationsState {
  switch (action.type) {
    case "reset":
      return EMPTY_STATE;

    // The server counts unread over up to `cap` stored notifications and returns
    // only `limit` of them, so the badge is *its* number — never a count of the
    // local list, which would cap every busy account's badge at the page size.
    case "sync":
      return {
        items: action.items,
        unreadCount: Math.max(0, action.unreadCount),
      };

    case "ingest": {
      // The de-duplication now governs the count too: a notification the last
      // refresh already delivered adds nothing at all.
      if (state.items.some((n) => n.id === action.notification.id)) return state;
      const items = [action.notification, ...state.items].slice(0, MAX_CAP);
      const unread = action.notification.readAt ? 0 : 1;
      return {
        items,
        // At exactly the cap the server evicts as it inserts, so an unclamped
        // +1 would outrun a total that cannot grow.
        unreadCount: Math.min(MAX_CAP, state.unreadCount + unread),
      };
    }

    // Used twice for the same ids: optimistically before the request, and again
    // when it commits. The second pass is normally a no-op, and matters only when
    // a list fetched *before* the write landed in between and put the item back
    // (code review #2) — applying it again is safe precisely because it only ever
    // touches an item that is present and still unread.
    case "read": {
      let stamped = 0;
      const items = state.items.map((n) => {
        if (!action.ids.includes(n.id) || n.readAt) return n;
        stamped += 1;
        return { ...n, readAt: action.readAt };
      });
      if (!stamped) return state;
      return { items, unreadCount: Math.max(0, state.unreadCount - stamped) };
    }

    // The rollback of a failed `markRead`. Targeted on purpose: restoring a
    // whole-state snapshot would erase a notification ingested, or another item
    // read, while the failed request was in flight.
    case "restoreRead": {
      const target = state.items.find((n) => n.id === action.id);
      if (!target) return state;
      if (!!target.readAt === !!action.prevReadAt) return state;
      return {
        items: state.items.map((n) =>
          n.id === action.id ? { ...n, readAt: action.prevReadAt } : n
        ),
        unreadCount: action.prevReadAt
          ? Math.max(0, state.unreadCount - 1)
          : Math.min(MAX_CAP, state.unreadCount + 1),
      };
    }

    case "markAllRead":
      return {
        items: state.items.map((n) => ({
          ...n,
          readAt: n.readAt ?? action.readAt,
        })),
        unreadCount: 0,
      };

    default:
      return state;
  }
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, entityType, entityId, user } = useAuth();
  const { i18n } = useTranslation();

  const [state, dispatch] = useReducer(notificationsReducer, EMPTY_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const limitRef = useRef(limit);
  limitRef.current = limit;

  const stateRef = useRef(state);
  stateRef.current = state;

  // `refresh` is memoised on the auth identity alone, so the language has to
  // reach it through a ref — read from the closure it would stay whatever it
  // was when the provider mounted.
  const langRef = useRef(i18n.language);
  langRef.current = i18n.language;

  // Important: notifications are scoped to the *current auth identity* (user/entity) and project.
  // When admin switches impersonation target, we must reset + refetch.
  const scopeKey = useMemo(() => {
    if (!isAuthenticated) return null;
    if (entityType && entityId) return `entity:${entityType}:${entityId}`;
    if (user?.id)
      return `user:${user.id}:${user.selectedProjectId || "global"}`;
    return "unknown";
  }, [
    isAuthenticated,
    entityType,
    entityId,
    user?.id,
    user?.selectedProjectId,
  ]);

  /**
   * The provider outlives an entity switch, so a GET or a rollback issued under
   * entity A can land after the reset and paint A's notifications under B's name.
   * Every async operation captures the scope it started in.
   */
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;

  /**
   * Bumped by everything that changes read state locally. A GET that started
   * before such a change carries a snapshot that predates it — applying it would
   * resurrect a notification the user just read.
   */
  const mutationSeqRef = useRef(0);
  /**
   * Two GETs can be in the air at once — a language switch and a socket event,
   * say — and the network decides which lands last. Without an ordering the
   * older snapshot wins: it restores the previous language's text, drops the
   * notification that arrived in between and walks the badge backwards (code
   * review). Every request takes a ticket; only a newer ticket may paint.
   */
  const requestSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  const refreshRetryRef = useRef(false);
  const refreshRef = useRef<((nextLimit?: number) => Promise<void>) | null>(
    null
  );

  const refresh = useCallback(
    async (nextLimit?: number) => {
      if (!isAuthenticated) return;
      const scope = scopeRef.current;
      const seq = mutationSeqRef.current;
      const ticket = ++requestSeqRef.current;
      const effectiveLimit = Math.max(
        1,
        Math.min(MAX_CAP, nextLimit ?? limitRef.current)
      );
      setIsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/notifications`, {
          // The UI language, not the browser's: the server renders the stored
          // notifications in it (BUG-1).
          params: { limit: effectiveLimit, lang: langRef.current },
          withCredentials: true,
        });
        if (scopeRef.current !== scope) return;
        if (mutationSeqRef.current !== seq) {
          // Stale snapshot. Drop it and ask once more, so the badge still ends
          // up on the server's number rather than on a pre-mutation one.
          if (!refreshRetryRef.current) {
            refreshRetryRef.current = true;
            try {
              await refreshRef.current?.(effectiveLimit);
            } finally {
              refreshRetryRef.current = false;
            }
          }
          return;
        }
        if (res.data?.success) {
          // A response older than one already painted is stale by definition.
          if (ticket < appliedSeqRef.current) return;
          appliedSeqRef.current = ticket;
          const data = res.data.data as NotificationsResponse;
          dispatch({
            type: "sync",
            items: data.notifications || [],
            unreadCount: data.unreadCount || 0,
          });
          setLimit(effectiveLimit);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated]
  );
  refreshRef.current = refresh;

  const loadMore = useCallback(async () => {
    const nextLimit = Math.min(MAX_CAP, limit + 20);
    await refresh(nextLimit);
  }, [limit, refresh]);

  const markRead = useCallback(
    async (id: string) => {
      if (!isAuthenticated) return;
      const scope = scopeRef.current;
      const previous = stateRef.current.items.find((n) => n.id === id);
      const wasUnread = !!previous && !previous.readAt;

      // Optimistic *before* the request, not in `finally`: the Header dropdown
      // navigates away on the same click, so the feedback has to be instant —
      // but a failure must leave no trace. Clicking an already-read item (which
      // `Header` does on every click) changes nothing at all.
      if (wasUnread) {
        mutationSeqRef.current += 1;
        dispatch({ type: "read", ids: [id], readAt: new Date().toISOString() });
      }

      try {
        await axios.post(
          `${API_URL}/api/notifications/${id}/read`,
          {},
          { withCredentials: true }
        );
        // Re-assert once the write has committed. A list fetched before it
        // committed can have landed in the meantime and put the item back
        // unread; without this the badge stays wrong until the next refresh.
        if (scopeRef.current !== scope || !wasUnread) return;
        mutationSeqRef.current += 1;
        dispatch({ type: "read", ids: [id], readAt: new Date().toISOString() });
      } catch (error) {
        console.error("Error marking notification read:", error);
        if (scopeRef.current !== scope || !wasUnread) return;
        mutationSeqRef.current += 1;
        dispatch({ type: "restoreRead", id, prevReadAt: null });
        // A lost response does not mean the write did not commit — ask.
        void refresh();
      }
    },
    [isAuthenticated, refresh]
  );

  const markAllRead = useCallback(async () => {
    if (!isAuthenticated) return;
    const scope = scopeRef.current;
    mutationSeqRef.current += 1;
    dispatch({ type: "markAllRead", readAt: new Date().toISOString() });

    try {
      await axios.post(
        `${API_URL}/api/notifications/read-all`,
        {},
        { withCredentials: true }
      );
      if (scopeRef.current !== scope) return;
      // Ask, rather than patch (code review r2 #2). "Everything is read" is a
      // claim about rows beyond the loaded page: with 50 unread and a page of
      // 20, re-asserting the 20 ids we can see leaves the badge at 30 while the
      // server has 0. Bumping the sequence first invalidates any list fetched
      // before the write committed, and a notification that arrived during the
      // request comes back correctly unread.
      mutationSeqRef.current += 1;
      void refresh();
    } catch (error) {
      console.error("Error marking all notifications read:", error);
      if (scopeRef.current !== scope) return;
      // The prior per-item stamps are gone, so there is nothing to restore
      // item by item; the server is the authority.
      void refresh();
    }
  }, [isAuthenticated, refresh]);

  const ingest = useCallback((notification: AppNotification) => {
    mutationSeqRef.current += 1;
    dispatch({ type: "ingest", notification });
  }, []);

  // Reset + load whenever auth scope changes (logout / switch entity / switch project)
  useEffect(() => {
    // Always reset local state first so UI doesn't show the previous entity's notifications.
    dispatch({ type: "reset" });
    setLimit(DEFAULT_LIMIT);

    if (!isAuthenticated) return;
    if (!scopeKey) return;

    refresh(DEFAULT_LIMIT);
  }, [isAuthenticated, scopeKey, refresh]);

  // The text is rendered server-side, so switching the interface language has
  // to re-ask for the list — otherwise the panel keeps the previous language
  // until the next event (BUG-1). Skipped on the first run: the effect above
  // has just loaded the list in that same language.
  const loadedLangRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !scopeKey) {
      loadedLangRef.current = null;
      return;
    }
    if (loadedLangRef.current === null) {
      loadedLangRef.current = i18n.language;
      return;
    }
    if (loadedLangRef.current === i18n.language) return;
    loadedLangRef.current = i18n.language;
    refresh();
  }, [i18n.language, isAuthenticated, scopeKey, refresh]);

  const value = useMemo(
    () => ({
      notifications: state.items,
      unreadCount: state.unreadCount,
      isLoading,
      limit,
      refresh,
      loadMore,
      markRead,
      markAllRead,
      ingest,
    }),
    [
      state.items,
      state.unreadCount,
      isLoading,
      limit,
      refresh,
      loadMore,
      markRead,
      markAllRead,
      ingest,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within NotificationsProvider"
    );
  }
  return ctx;
}
